import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  AlertTriangle, TrendingUp, TrendingDown, Clock, Target, 
  CheckCircle, XCircle, AlertCircle, Brain, Filter,
  ChevronDown, ChevronUp, Search
} from 'lucide-react';

// Score circle component
const ScoreCircle = ({ score, label, color, size = "large" }) => {
  const sizeClasses = {
    large: "w-32 h-32 text-4xl",
    medium: "w-20 h-20 text-2xl",
    small: "w-14 h-14 text-lg"
  };
  
  const colorClasses = {
    green: "bg-gradient-to-br from-emerald-400 to-emerald-600",
    blue: "bg-gradient-to-br from-blue-400 to-blue-600",
    yellow: "bg-gradient-to-br from-amber-400 to-amber-600",
    orange: "bg-gradient-to-br from-orange-400 to-orange-600",
    red: "bg-gradient-to-br from-red-400 to-red-600"
  };
  
  return (
    <div className="flex flex-col items-center">
      <div className={`${sizeClasses[size]} ${colorClasses[color]} rounded-full flex items-center justify-center text-white font-bold shadow-lg`}>
        {score}
      </div>
      {label && <p className="text-sm text-muted-foreground mt-2">{label}</p>}
    </div>
  );
};

// Metric Card component
const MetricCard = ({ title, value, subtitle, color, icon: Icon, badge }) => {
  const colorBg = {
    green: "border-l-emerald-500 bg-emerald-50/50",
    yellow: "border-l-amber-500 bg-amber-50/50",
    orange: "border-l-orange-500 bg-orange-50/50",
    red: "border-l-red-500 bg-red-50/50",
    blue: "border-l-blue-500 bg-blue-50/50"
  };
  
  const iconColor = {
    green: "text-emerald-600",
    yellow: "text-amber-600",
    orange: "text-orange-600",
    red: "text-red-600",
    blue: "text-blue-600"
  };
  
  return (
    <Card className={`border-l-4 ${colorBg[color]}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="flex flex-col items-end gap-2">
            {Icon && <Icon className={`w-6 h-6 ${iconColor[color]}`} />}
            {badge && (
              <Badge variant="destructive" className="text-xs">
                {badge}
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Task row component
const TaskRow = ({ task, index }) => {
  const getBgColor = () => {
    if (task.is_over_1h) return "bg-red-50";
    if (task.exceeds_estimate) return "bg-orange-50";
    if (task.is_vague) return "bg-yellow-50";
    return "";
  };
  
  const formatMinutes = (minutes) => {
    if (!minutes) return "-";
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };
  
  const formatDifference = (diff) => {
    if (!diff) return "-";
    const sign = diff > 0 ? "+" : "";
    return `${sign}${formatMinutes(Math.abs(diff))}`;
  };
  
  return (
    <tr className={`border-b hover:bg-slate-50 ${getBgColor()}`}>
      <td className="py-2 px-3 text-sm text-muted-foreground">{index + 1}</td>
      <td className="py-2 px-3">
        <p className="font-medium text-sm truncate max-w-[200px]" title={task.title}>
          {task.title}
        </p>
      </td>
      <td className="py-2 px-3 text-sm">
        <p className="truncate max-w-[150px]" title={task.project_name}>
          {task.project_name}
        </p>
        {task.client_name && (
          <p className="text-xs text-muted-foreground truncate">{task.client_name}</p>
        )}
      </td>
      <td className="py-2 px-3 text-sm">{task.date || "-"}</td>
      <td className="py-2 px-3 text-sm text-center">{formatMinutes(task.estimated_minutes)}</td>
      <td className="py-2 px-3 text-sm text-center font-medium">{formatMinutes(task.actual_minutes)}</td>
      <td className={`py-2 px-3 text-sm text-center font-medium ${task.difference > 0 ? 'text-red-600' : task.difference < 0 ? 'text-green-600' : ''}`}>
        {task.estimated_minutes > 0 ? formatDifference(task.difference) : "-"}
      </td>
      <td className="py-2 px-3 text-sm">
        <Badge variant="outline" className="text-xs capitalize">
          {task.status}
        </Badge>
      </td>
      <td className="py-2 px-3">
        <div className="flex gap-1">
          {task.is_over_1h && (
            <span title="Peste 1 oră" className="text-orange-500">🟠</span>
          )}
          {task.exceeds_estimate && (
            <span title="Depășește estimarea" className="text-red-500">🔴</span>
          )}
          {task.is_vague && (
            <span title="Denumire vagă" className="text-yellow-500">🟡</span>
          )}
        </div>
      </td>
    </tr>
  );
};

// AI Analysis Section
const AIAnalysisSection = ({ analysis }) => {
  if (!analysis) return null;
  
  const riskColors = {
    "Ridicat": "bg-red-100 text-red-800 border-red-300",
    "Mediu": "bg-orange-100 text-orange-800 border-orange-300",
    "Scăzut": "bg-green-100 text-green-800 border-green-300"
  };
  
  return (
    <Card className="border-purple-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-purple-700">
          <Brain className="w-5 h-5" />
          Analiză Comportamentală AI
        </CardTitle>
        <CardDescription>Pattern-uri detectate și recomandări pentru manager</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Risk Level */}
        <div className={`p-3 rounded-lg border ${riskColors[analysis.risk_level]}`}>
          <p className="font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            NIVEL RISC: {analysis.risk_level}
          </p>
        </div>
        
        {/* Recommendations */}
        {analysis.recommendations?.length > 0 && (
          <div className="space-y-2">
            <p className="font-semibold text-slate-700">Acțiuni recomandate pentru manager:</p>
            <ul className="space-y-2">
              {analysis.recommendations.map((rec, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 p-2 rounded">
                  <span className="text-orange-500 mt-0.5">→</span>
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Detected Patterns */}
        {analysis.patterns_detected?.length > 0 && (
          <div className="space-y-2">
            <p className="font-semibold text-slate-700">Pattern-uri detectate:</p>
            <div className="flex flex-wrap gap-2">
              {analysis.patterns_detected.map((pattern, idx) => (
                <Badge 
                  key={idx} 
                  variant={pattern.severity === 'high' ? 'destructive' : 'outline'}
                  className="text-xs"
                >
                  {pattern.description}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {/* Strengths */}
        {analysis.strengths?.length > 0 && (
          <div className="space-y-2">
            <p className="font-semibold text-emerald-700">Puncte forte identificate:</p>
            <ul className="space-y-1">
              {analysis.strengths.map((strength, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm text-emerald-600">
                  <CheckCircle className="w-4 h-4" />
                  {strength}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* No issues */}
        {analysis.recommendations?.length === 0 && analysis.patterns_detected?.length === 0 && (
          <div className="text-center py-4 text-emerald-600">
            <CheckCircle className="w-8 h-8 mx-auto mb-2" />
            <p>Nu au fost detectate pattern-uri comportamentale negative</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main component
export default function EmployeeEvaluation({ employeeId, month }) {
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [projectFilter, setProjectFilter] = useState('all');
  const [problemFilter, setProblemFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  
  useEffect(() => {
    if (employeeId && month) {
      loadEvaluation();
    }
  }, [employeeId, month]);
  
  const loadEvaluation = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/employee-evaluation/${employeeId}?month=${month}`);
      setEvaluation(response.data);
    } catch (err) {
      console.error('Error loading evaluation:', err);
      setError(err.response?.data?.detail || 'Eroare la încărcarea evaluării');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <p className="text-red-600">{error}</p>
          <Button onClick={loadEvaluation} className="mt-4">Încearcă din nou</Button>
        </CardContent>
      </Card>
    );
  }
  
  if (!evaluation) return null;
  
  const { metrics, tasks, task_summary, ai_analysis } = evaluation;
  
  // Check if there's no data
  const hasNoData = task_summary.total === 0 && metrics.utilization.worked_minutes === 0;
  
  // Get unique projects for filter
  const uniqueProjects = [...new Set(tasks.map(t => t.project_name))].filter(Boolean);
  
  // Filter and sort tasks
  let filteredTasks = [...tasks];
  
  if (projectFilter !== 'all') {
    filteredTasks = filteredTasks.filter(t => t.project_name === projectFilter);
  }
  
  if (problemFilter === 'problems') {
    filteredTasks = filteredTasks.filter(t => t.is_over_1h || t.exceeds_estimate || t.is_vague);
  }
  
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    filteredTasks = filteredTasks.filter(t => 
      t.title.toLowerCase().includes(term) || 
      t.project_name?.toLowerCase().includes(term)
    );
  }
  
  // Sort
  filteredTasks.sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'date':
        comparison = (a.date || '').localeCompare(b.date || '');
        break;
      case 'actual':
        comparison = (a.actual_minutes || 0) - (b.actual_minutes || 0);
        break;
      case 'difference':
        comparison = (a.difference || 0) - (b.difference || 0);
        break;
      default:
        comparison = 0;
    }
    return sortDir === 'desc' ? -comparison : comparison;
  });
  
  return (
    <div className="space-y-6" data-testid="employee-evaluation-tab">
      {/* No data warning */}
      {hasNoData && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-700">
              <AlertCircle className="w-5 h-5" />
              <span>Nu există date înregistrate pentru {evaluation.employee_name} în luna {evaluation.period}. Verificați că există time entries pentru această perioadă.</span>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* SECTION 1: Summary Metrics */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-blue-600" />
          Sumar Metrici - {evaluation.period}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Card 1: Capacity Usage */}
          <MetricCard
            title="Utilizare Capacity Planning"
            value={`${metrics.utilization.percentage}%`}
            subtitle={`${metrics.utilization.worked_hours}h lucrate din ${metrics.utilization.allocated_hours}h alocate`}
            color={metrics.utilization.color}
            icon={Clock}
          />
          
          {/* Card 2: Exceeding Tasks */}
          <MetricCard
            title="Taskuri Timp Excedentar"
            value={`${metrics.exceeding_tasks.count} taskuri`}
            subtitle={`${metrics.exceeding_tasks.percentage}% din total`}
            color={metrics.exceeding_tasks.color}
            icon={TrendingUp}
          />
          
          {/* Card 3: Over 1h Tasks */}
          <MetricCard
            title="Taskuri Peste 1 Oră"
            value={`${metrics.over_1h_tasks.count} taskuri`}
            subtitle={metrics.over_1h_tasks.is_risk ? "Risc detectat" : "În limite normale"}
            color={metrics.over_1h_tasks.is_risk ? "red" : "green"}
            icon={AlertCircle}
            badge={metrics.over_1h_tasks.is_risk ? "RISC" : null}
          />
          
          {/* Card 4: Underperformance Days */}
          <MetricCard
            title="Zile Sub-Performanță"
            value={`${metrics.underperformance_days.count} zile`}
            subtitle={`din ${metrics.underperformance_days.total_days} zile lucrate`}
            color={metrics.underperformance_days.color}
            icon={TrendingDown}
          />
          
          {/* Card 5: General Score */}
          <Card className="border-2 border-slate-200">
            <CardContent className="p-4 flex flex-col items-center justify-center">
              <p className="text-sm text-muted-foreground mb-2">Scor General</p>
              <ScoreCircle 
                score={metrics.general_score.value} 
                label={metrics.general_score.label}
                color={metrics.general_score.color}
                size="medium"
              />
            </CardContent>
          </Card>
        </div>
        
        {/* Score Breakdown */}
        <Card className="mt-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Defalcarea Scorului</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Utilizare CP (30p)</p>
                <Progress value={(metrics.general_score.breakdown.utilization / 30) * 100} className="h-2 mt-1" />
                <p className="text-xs font-medium mt-1">{metrics.general_score.breakdown.utilization.toFixed(1)} / 30</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Fără depășire (25p)</p>
                <Progress value={(metrics.general_score.breakdown.no_exceed / 25) * 100} className="h-2 mt-1" />
                <p className="text-xs font-medium mt-1">{metrics.general_score.breakdown.no_exceed.toFixed(1)} / 25</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Sub 1h (20p)</p>
                <Progress value={(metrics.general_score.breakdown.under_1h / 20) * 100} className="h-2 mt-1" />
                <p className="text-xs font-medium mt-1">{metrics.general_score.breakdown.under_1h.toFixed(1)} / 20</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">La capacitate (25p)</p>
                <Progress value={(metrics.general_score.breakdown.at_capacity / 25) * 100} className="h-2 mt-1" />
                <p className="text-xs font-medium mt-1">{metrics.general_score.breakdown.at_capacity.toFixed(1)} / 25</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* SECTION 2: Task List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            Lista Completă Taskuri
          </CardTitle>
          <CardDescription>
            {task_summary.total} taskuri total | {task_summary.over_1h} peste 1h | {task_summary.exceeding_estimate} depășesc estimarea | {task_summary.vague} vagi
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Caută task..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-48 h-8"
              />
            </div>
            
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-48 h-8">
                <SelectValue placeholder="Toate proiectele" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate proiectele</SelectItem>
                {uniqueProjects.map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={problemFilter} onValueChange={setProblemFilter}>
              <SelectTrigger className="w-48 h-8">
                <SelectValue placeholder="Toate taskurile" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate taskurile</SelectItem>
                <SelectItem value="problems">Doar problematice</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-36 h-8">
                <SelectValue placeholder="Sortare" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">După dată</SelectItem>
                <SelectItem value="actual">După timp real</SelectItem>
                <SelectItem value="difference">După diferență</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
              className="h-8"
            >
              {sortDir === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </Button>
          </div>
          
          {/* Legend */}
          <div className="flex gap-4 mb-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-red-50 border"></span> Peste 1h</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-orange-50 border"></span> Depășește estimarea</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-yellow-50 border"></span> Denumire vagă</span>
          </div>
          
          {/* Table */}
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="border-b">
                  <th className="py-2 px-3 text-xs font-medium text-muted-foreground">#</th>
                  <th className="py-2 px-3 text-xs font-medium text-muted-foreground">Task</th>
                  <th className="py-2 px-3 text-xs font-medium text-muted-foreground">Proiect / Client</th>
                  <th className="py-2 px-3 text-xs font-medium text-muted-foreground">Dată</th>
                  <th className="py-2 px-3 text-xs font-medium text-muted-foreground text-center">Estimat</th>
                  <th className="py-2 px-3 text-xs font-medium text-muted-foreground text-center">Real</th>
                  <th className="py-2 px-3 text-xs font-medium text-muted-foreground text-center">Diferență</th>
                  <th className="py-2 px-3 text-xs font-medium text-muted-foreground">Status</th>
                  <th className="py-2 px-3 text-xs font-medium text-muted-foreground">Flag</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task, idx) => (
                  <TaskRow key={task.id || idx} task={task} index={idx} />
                ))}
                {filteredTasks.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-muted-foreground">
                      Nu există taskuri care să corespundă filtrelor
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* SECTION 3: AI Analysis */}
      <AIAnalysisSection analysis={ai_analysis} />
    </div>
  );
}
